import React from 'react';

import { SidePage, Section, IconButton, GetAppIcon } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useScreenGo } from '@gogame/screens';

import AffiliateNote from 'scenes/Campaigns/components/AffiliateNote';
import { CampaignActions } from 'scenes/Campaigns/GGCampaigns/components/CampaignActions';
import TrackingLinks from 'scenes/Campaigns/GGCampaigns/components/TrackingLinks';
import { useLoadCampaignDetails } from 'scenes/Campaigns/GGCampaigns/data';
import { useExportLinks } from 'scenes/Campaigns/GGCampaigns/data/mutations';
import { useCopyLink } from 'scenes/Campaigns/hooks';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import PromoCodeDetails from 'scenes/PromoCodes/components/PromoCodeDetails';
import ParentCampaignDetails from 'scenes/Campaigns/ParentCampaigns/components/ParentCampaignDetails';
import { parentCampaignDetailsLink } from 'scenes/Campaigns/ParentCampaigns/navigation';

import { FraudProtection } from 'scenes/Campaigns/components/Details/FraudProtection';
import {
  CampaignFormDuplicateWithChaining,
  CampaignFormEdit,
} from '../CampaignForm';
import { MoveToAnotherAffiliate } from '../CampaignForm/MoveToAnotherAffiliate';
import { FILTER_CONVERSION_PAGE_IDS } from '../TrackingLinks/constants';
import { CostModels } from './CostModels';
import GGLinkSection from './GGLinkSection';
import General from './General';
import Metadata from './Metadata';

export const CampaignDetails = ({ id, onClose, onExited, open }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isRefetching } = useLoadCampaignDetails({ id });
  const costModelsCount = data?.costModels?.length;

  const copyLink = useCopyLink();
  const go = useScreenGo();
  const [exportLinks] = useExportLinks();

  const handleEdit = ({ id, name }) => {
    openChildComponent(CampaignFormEdit, {
      name: name,
      id: id,
    });
  };
  const handleMoveToAnotherAffiliate = ({ id, name }) => {
    openChildComponent(MoveToAnotherAffiliate, {
      name: name,
      id: id,
    });
  };
  const handleDuplicate = ({ id }) => {
    openChildComponent(CampaignFormDuplicateWithChaining, {
      id: id,
      onSubmit: (campaign) => {
        if (campaign?.parentCampaign?.id) {
          go(
            `../../${parentCampaignDetailsLink.absolutePath.replace(':id', campaign.parentCampaign.id)}`
          );
          return;
        }
        go(`../${campaign.id}`);
      },
    });
  };

  const handleOpenAffiliate = (_e, { id }) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleGenerateLinks = ({ id, application, network }) => {
    openChildComponent(TrackingLinks, {
      id,
      applicationId: application?.id,
      networkId: network?.id,
    });
  };

  const handleOpenPromoCodeDetails = (id) => {
    openChildComponent(PromoCodeDetails, {
      id,
    });
  };

  const handleOpenParentCampaignDetails = (_, { id }) => {
    openChildComponent(ParentCampaignDetails, {
      id,
    });
  };

  const fraudCount = data?.fraudProtections?.length || 0;

  const handleExport = () => {
    exportLinks({
      [FILTER_CONVERSION_PAGE_IDS]: data?.conversionPages.map(
        ({ conversionPageId }) => conversionPageId
      ),
      ...(id ? { campaignIds: [id] } : null),
    });
  };
  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'GG Campaign'}
        entityId={id}
        subTitle={data?.name}
      />
      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent withSidebar={true}>
          <Section title={'General'}>
            <General
              data={data}
              onOpenAffiliate={handleOpenAffiliate}
              onOpenPromoCodeDetails={handleOpenPromoCodeDetails}
              onOpenParentCampaignDetails={handleOpenParentCampaignDetails}
              onCopyLink={copyLink}
            />
          </Section>
          {data?.link && (
            <Section
              title={'Campaign Links'}
              isCollapsable
              initialOpen={false}
              action={
                <IconButton
                  icon={<GetAppIcon />}
                  tooltip={'Export links'}
                  onClick={handleExport}
                />
              }
            >
              <GGLinkSection campaignLinks={data?.campaignLinks} />
            </Section>
          )}
          <Section
            title={`Cost Models (${costModelsCount || 0})`}
            isCollapsable={!!costModelsCount}
            initialOpen={false}
          >
            <CostModels
              models={data?.costModels}
              campaignId={id}
              fullWidthItem
              editable={data?.actions?.includes('edit')}
            />
          </Section>
          {data?.fraudProtections?.length ? (
            <Section
              title={`Fraud Protection (${fraudCount})`}
              isCollapsable={!!fraudCount}
              initialOpen={false}
            >
              <FraudProtection fraudProtections={data?.fraudProtections} />
            </Section>
          ) : null}
          {data && (
            <Section>
              <AffiliateNote />
            </Section>
          )}
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <CampaignActions
              limit={3}
              data={data}
              actions={data?.actions}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onGenerateLinks={handleGenerateLinks}
              onMoveToAnotherAffiliate={handleMoveToAnotherAffiliate}
            />
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default CampaignDetails;
